import { Alert, Badge, Button, buttonClass, cardClass, fieldClass, Input, Select, Table, Textarea } from '@ledgerloom/ui';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type Outcome, type Party } from '../api';
import { useApi, usePageTitle, useStored } from '../hooks';
import { hasSurface, useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Expandable, Icon, ListSkeleton, Loading, outcomeMessage, outcomeTone, schemaLabel, Score, Time, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AskAgent } from '../components/AskAgent';
import { fetchParty, forgetParty, PartyName } from '../components/PartyName';
import { money, systemName } from '../format';
import { SiteChangeBody } from './SiteChange';

// The inbox item contract (docs/design/inbox.md): every item says what it is,
// why it reached you, and what you can do, and every action runs from here.
export interface Fact { label: string; value: string; href?: string | null; technical?: boolean }
export interface ActionInput { name: string; label: string; type: 'text' | 'textarea' | 'number' | 'select'; required: boolean; hint?: string | null; options?: Array<{ value: string; label: string }> | null; default?: string | null }
export interface InboxAction { id: string; label: string; effect: string; tone: 'primary' | 'danger' | 'plain'; confirm: boolean; inputs: ActionInput[]; disabled_reason: string | null }
interface Proposal { id: string; schema_id: string; payload: any; computed: any; rationale: string | null }
export type StepState = 'pending' | 'authorized' | 'executing' | 'completed' | 'failed';
export interface Lane { group: string; name: string; unlock: string; steps: Array<{ label: string; state: StepState; detail: string; href: string | null }> }
export interface InboxItem {
  id: string; source: 'approval' | 'exception'; kind: string; subject: string; topic: string; title: string;
  from: { kind: 'agent' | 'person' | 'system'; name: string; agent_id: string | null };
  meaning: string; facts: Fact[]; actions: InboxAction[]; created_at: string; expires_at: string | null;
  flags: { escalated: boolean; escalation_reason: string | null; stale_reason: string | null; signoffs: { have: number; need: number } | null };
  proposal: Proposal | null;
  asks: Array<{ id: string; text: string }>;
  impacts: Array<{ label: string; now: string; approve: string; reject: string }>;
  unlocks: Lane[];
  // ADR 0039: an agent's redraft after someone asked for changes.
  revision: { of: string; note: string; changes: Array<{ label: string; before: string; after: string }> } | null;
}
export interface Subject { id: string; label: string; count: number }
export interface Inbox { subjects: Subject[]; items: InboxItem[]; counts: { items: number; approvals: number; exceptions: number }; advisers: Array<{ id: string; name: string }> }

// A link into the inbox with one item open.
export const inboxItem = (id: string) => `/desk/needs-you?item=${encodeURIComponent(id)}`;

// The queue's groups, first match wins: problems pinned on top in their own group, never mixed
// with sign-offs (ruled 2026-10-28), then sign-offs escalated or stale, then the rest to decide.
const BANDS: Array<{ id: string; label: string; has: (i: InboxItem) => boolean }> = [
  { id: 'problems', label: 'Problems', has: (i) => i.source === 'exception' },
  { id: 'urgent', label: 'Urgent', has: (i) => i.flags.escalated || !!i.flags.stale_reason },
  { id: 'decide', label: 'To decide', has: () => true },
];
const bandOf = (i: InboxItem) => BANDS.find((b) => b.has(i))!.id;
// Every word of the filter appears in the item's topic, title, or who raised it.
const matches = (i: InboxItem, q: string) => { const hay = `${i.topic} ${i.title} ${i.from.name}`.toLowerCase(); return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w)); };

// The inbox as a queue and one card (docs/design/inbox.md, reworked 2026-10-27
// after the unlock mock): the queue grouped by what needs doing first, and the
// open item with what it is, what changes either way, what it sets moving, why
// it reached you, and its actions along the foot. Subject, filter, and item are
// held in the URL. Patterns: data-display/list-view, data-display/filter-panel,
// forms/button with content-management/modal to confirm, ai-intelligence/ai-suggestions.
export function Approvals() {
  const [params, setParams] = useSearchParams();
  const subject = params.get('subject');
  const itemId = params.get('item');
  const q = params.get('q') ?? '';
  const standing = params.get('standing') === '1';
  const { data, loading, error, reload } = useApi<Inbox>('/v1/inbox');
  usePageTitle('Needs you');
  // New items land while it is open: an agent's draft, a teammate's request.
  useEffect(() => { const t = setInterval(() => void reload(), 10_000); return () => clearInterval(t); }, [reload]);
  const all = data?.items ?? [];
  const nodes = data?.subjects ?? [];
  const here = nodes.find((n) => n.id === subject) ?? null;
  const items = all.filter((i) => (!here || i.subject === here.id) && matches(i, q));
  const open = all.find((i) => i.id === itemId) ?? null;
  const [focusNext, setFocusNext] = useState(false);
  // Problems start folded; whether this browser last left them open is remembered (ruled 2026-10-28).
  const [problemsOpen, setProblemsOpen] = useStored('ll.inbox.problems.open', false);

  const go = (patch: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) if (v === null || v === '') next.delete(k); else next.set(k, v);
    setParams(next, { replace });
  };
  // After an action the item leaves; the next one in the queue opens, or the queue heading takes focus.
  const done = (id: string) => {
    const at = items.findIndex((i) => i.id === id);
    const next = items[at + 1] ?? items[at - 1] ?? null;
    go({ item: next && next.id !== id ? next.id : null }, true);
    setFocusNext(true);
    void reload();
  };
  useEffect(() => {
    if (!focusNext || loading) return;
    setFocusNext(false);
    document.querySelector<HTMLElement>('#inbox-item-h, #inbox-list-h')?.focus();
  }, [focusNext, loading]);

  const label = here?.label ?? 'Everything';
  const link = (id: string) => `?${new URLSearchParams({ ...(here ? { subject: here.id } : {}), ...(q ? { q } : {}), item: id })}`;
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/desk', label: 'Agent Desk' }, { label: 'Needs you' }]} />
      <div className="page-head">
        <div><h1>Needs you</h1><p>Things that need you. Each one says what happened and what you can do about it, right here.</p></div>
        <div className="row">
          {data && <span className="faint" role="status">{data.counts.items} waiting · {data.counts.approvals} to approve · {data.counts.exceptions} {data.counts.exceptions === 1 ? 'problem' : 'problems'}</span>}
          <Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="?standing=1" aria-current={standing ? 'page' : undefined}>Decided for you</Link>
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className={`deck${open || standing ? ' has-item' : ''}`}>
        <section className="deck-queue" aria-labelledby="inbox-list-h">
          <div className="deck-finder">
            <h2 id="inbox-list-h" tabIndex={-1} className="sr-only">{label}</h2>
            <label className="sr-only" htmlFor="inbox-q">Filter by topic, title, or who is asking</label>
            <input id="inbox-q" className={fieldClass} type="search" placeholder="Filter by topic, title, or who is asking" value={q} onChange={(e) => go({ q: e.target.value, item: null }, true)} />
            <Select id="inbox-subject" label="From" value={here?.id ?? ''} onValueChange={(v) => go({ subject: v || null, item: null }, true)}
              options={[{ value: '', label: `Everyone (${all.length})` }, ...nodes.map((n) => ({ value: n.id, label: `${n.label}${n.count ? ` (${n.count})` : ''}` }))]} />
          </div>
          {loading && !data ? <ListSkeleton rows={4} label="Loading what needs you" /> : !items.length ? (
            error ? null : <Empty title={q ? 'Nothing matches that filter' : here ? `Nothing waiting from ${label}` : "You're all caught up"} action={<Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="/desk/in-motion">See what is in motion</Link>}>When an agent or a teammate needs a person, it lands here.</Empty>
          ) : BANDS.map((b) => {
            const list = items.filter((i) => bandOf(i) === b.id);
            if (!list.length) return null;
            const heading = <>{b.label} <span className="faint">{list.length}</span></>;
            const shut = b.id === 'problems' && !problemsOpen;
            return (
              <div key={b.id} className={`deck-band ${b.id}`}>
                <h3 className="deck-band-h" id={`band-${b.id}`}>
                  {b.id === 'problems'
                    ? <button type="button" className="deck-band-toggle" aria-expanded={!shut} aria-controls={`band-${b.id}-rows`} onClick={() => setProblemsOpen(shut)}>{heading}</button>
                    : heading}
                </h3>
                <ul id={`band-${b.id}-rows`} className="deck-rows" hidden={shut} aria-label={`Items in ${label}, ${b.label.toLowerCase()}`}>
                  {list.map((i) => (
                    <li key={i.id}>
                      <Link className={`deck-row${i.id === itemId ? ' on' : ''}`} to={link(i.id)} aria-current={i.id === itemId ? 'true' : undefined}>
                        <span className="deck-row-topic">{i.topic}</span>
                        <span className="item-title">{i.title}</span>
                        <span className="item-sub">{i.from.name} · <Time iso={i.created_at} /></span>
                        <Flags item={i} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
        <section className="deck-stage" aria-label={standing ? 'Decided for you' : 'Open item'}>
          {(open || standing) && <Button variant="ghost" size="sm" className="deck-back" onClick={() => go({ item: null, standing: null })}><Icon name="back" />Back to the list</Button>}
          {standing ? <Standing /> : open ? (
            <ItemCard key={open.id} item={open} advisers={data?.advisers ?? []} onDone={() => done(open.id)} />
          ) : itemId && data ? (
            <Empty title="That item is settled">Someone acted on it, or it expired. <Link to="/desk/in-motion">See what it set moving</Link>.</Empty>
          ) : (
            <p className="muted small deck-hint">Choose an item to see what it is and what you can do.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function Flags({ item }: { item: InboxItem }) {
  const f = item.flags;
  if (!f.escalated && !f.stale_reason && !f.signoffs && item.from.kind !== 'agent') return null;
  return (
    <span className="row" style={{ gap: 6 }}>
      {item.from.kind === 'agent' && item.source === 'approval' && <Badge tone="info">{item.revision ? 'Redraft' : 'Agent proposal'}</Badge>}
      {f.signoffs && <Badge>{f.signoffs.have} of {f.signoffs.need} sign-offs</Badge>}
      {f.stale_reason && <Badge tone="warning" title={f.stale_reason}>Stale</Badge>}
      {f.escalated && <Badge tone="danger">Escalated</Badge>}
    </span>
  );
}

// The five states (ADR 0038), each a word and a mark, never colour alone.
export const STATE_WORD: Record<StepState, string> = { pending: 'Pending', authorized: 'Authorized', executing: 'Executing', completed: 'Completed', failed: 'Failed' };

// What a decision sets moving, as a map: the sign-off on the left, the lanes it
// unlocks to its right, grouped. The Team page's In motion tab draws the same map.
export function UnlockMap({ lanes, gate }: { lanes: Lane[]; gate: { word: string; tone: 'pending' | 'approved' | 'rejected'; detail: string } }) {
  const groups = [...new Set(lanes.map((l) => l.group))];
  return (
    <div className="stack" style={{ gap: 10 }}>
      <ul className="unlock-legend" aria-label="What each state means">
        {(Object.keys(STATE_WORD) as StepState[]).map((s) => <li key={s}><span className={`unlock-mark st-${s}`} aria-hidden="true" />{STATE_WORD[s]}</li>)}
      </ul>
      <div className="unlock-map">
        <div className={`unlock-gate gate-${gate.tone}`}>
          <span className="unlock-word">{gate.word}</span>
          <strong>Your sign-off</strong>
          <p>{gate.detail}</p>
        </div>
        <div className="unlock-groups">
          {groups.map((g) => (
            <section key={g} aria-label={g}>
              <h4 className="unlock-group">{g}</h4>
              {lanes.filter((l) => l.group === g).map((l, n) => (
                <div key={`${l.name}-${n}`} className="unlock-lane">
                  <strong>{l.name}</strong>
                  <p className="faint small">{l.unlock}</p>
                  <ol className="unlock-steps" aria-label={l.name}>
                    {l.steps.map((s, i) => (
                      <li key={i} className={`unlock-step st-${s.state}`}>
                        <span className="unlock-word"><span className={`unlock-mark st-${s.state}`} aria-hidden="true" />{STATE_WORD[s.state]}</span>
                        <strong>{s.href ? <Link to={s.href}>{s.label}</Link> : s.label}</strong>
                        <p>{s.detail}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// The open item as one card, plain first for someone working through their day:
// who asks and what, what you're deciding, a chat about it, the detail, what
// changes either way, what it sets moving, why it needs you, then the actions
// along the foot. The machinery waits under Technical details.
function ItemCard({ item, advisers, onDone }: { item: InboxItem; advisers: Inbox['advisers']; onDone: () => void }) {
  const [note, setNote] = useState('');
  // A select shows its first option, so that is its value until the person picks another.
  const [inputs, setInputs] = useState<Record<string, Record<string, string>>>(() =>
    Object.fromEntries(item.actions.map((a) => [a.id, Object.fromEntries(a.inputs.map((i) => [i.name, i.default ?? (i.type === 'select' ? i.options?.[0]?.value : undefined) ?? '']))])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirming, setConfirming] = useState<InboxAction | null>(null);
  const toast = useToast();
  const { me, refreshStats, refreshMe } = useSession();
  const hid = 'inbox-item-h';
  const p = item.proposal;

  async function act(a: InboxAction) {
    setBusy(true); setError(null);
    try {
      const out = await api<{ closed: boolean; outcome: Outcome | null; routed: { agent_id: string } | null; note: string }>(
        `/v1/inbox/items/${encodeURIComponent(item.id)}/act`, { body: { action: a.id, note: note || undefined, inputs: inputs[a.id] } });
      setConfirming(null);
      if (!out.closed && out.outcome?.state !== 'pending_approval') { setError(new Error(outcomeMessage(out.outcome ?? { state: 'rejected' }))); return; }
      for (const id of [p?.payload?.survivor_id, p?.payload?.merged_id, p?.payload?.party_id].filter(Boolean)) forgetParty(id);
      toast(message(item, a, out, inputs[a.id]), out.outcome ? (a.id === 'reject' || a.id === 'changes' ? 'ok' : outcomeTone(out.outcome)) : 'ok');
      refreshStats();
      refreshMe();
      onDone();
    } catch (e) { setError(e); setConfirming(null); } finally { setBusy(false); }
  }
  // An action with inputs and a confirm asks for them in its dialog (the standing decision); others ask inline.
  const inDialog = (a: InboxAction) => a.confirm && (a.id === 'stand' || a.id === 'changes');
  const start = (a: InboxAction) => (a.confirm ? setConfirming(a) : void act(a));
  const setInput = (a: InboxAction) => (name: string, v: string) => setInputs((s) => ({ ...s, [a.id]: { ...s[a.id], [name]: v } }));
  const expires = item.expires_at ? new Date(item.expires_at) : null;
  const plain = item.facts.filter((f) => !f.technical);
  const technical = item.facts.filter((f) => f.technical);
  const status = item.flags.escalated ? { tone: 'danger', text: `Escalated: ${item.flags.escalation_reason}` }
    : item.flags.stale_reason ? { tone: 'warn', text: `Stale: ${item.flags.stale_reason}` }
    : item.flags.signoffs ? { tone: 'info', text: `${item.flags.signoffs.have} of ${item.flags.signoffs.need} sign-offs so far. Another person signs before anything is saved.` } : null;

  return (
    <article className="deck-card" aria-labelledby={hid}>
      <div className="deck-body stack">
        <header className="stack" style={{ gap: 6 }}>
          <p className="deck-kicker">{item.topic} · {item.from.name}{item.from.kind === 'agent' ? ' (agent)' : ''} · <Time iso={item.created_at} />{expires && <> · {item.source === 'approval' ? 'expires' : 'escalates'} <time dateTime={item.expires_at!} title={expires.toLocaleString()}>{expires.toLocaleString()}</time></>}</p>
          {status && <p className={`deck-status tone-${status.tone}`}>{status.text}</p>}
          <h2 id={hid} tabIndex={-1}>{item.title}</h2>
        </header>
        <section className="stack" style={{ gap: 4 }} aria-labelledby={`${hid}-what`}>
          <h3 id={`${hid}-what`} className="deck-h">What you're deciding</h3>
          <p className="deck-meaning">{item.meaning}</p>
        </section>
        {item.revision && (
          <section className="stack" style={{ gap: 8 }} aria-labelledby={`${hid}-changed`}>
            <h3 id={`${hid}-changed`} className="deck-h">What changed</h3>
            <p className="deck-meaning">{item.from.name} drafted this again after a note asking for changes{item.revision.note ? ':' : '.'}</p>
            {item.revision.note && <div className="note">{item.revision.note}</div>}
            {item.revision.changes.length ? (
              <Table label="What the new draft changed" tableClassName="deck-impacts">
                <caption className="sr-only">Each field the new draft changed</caption>
                <thead><tr><th scope="col"><span className="sr-only">What</span></th><th scope="col">Before</th><th scope="col" className="yes">Now</th></tr></thead>
                <tbody>{item.revision.changes.map((c) => <tr key={c.label}><th scope="row">{c.label}</th><td>{c.before}</td><td className="yes">{c.after}</td></tr>)}</tbody>
              </Table>
            ) : <p className="muted small">Nothing in the draft itself changed.</p>}
          </section>
        )}
        {hasSurface(me, 'inbox_ask') && <AskAbout key={item.id} itemId={item.id} advisers={advisers} />}
        {(plain.length > 0 || p) && (
          <section className="stack" style={{ gap: 8 }} aria-labelledby={`${hid}-detail`}>
            <h3 id={`${hid}-detail`} className="deck-h">The detail</h3>
            {p && <Body p={p} />}
            {plain.length > 0 && <dl className="facts">{plain.map((f) => <FactRow key={f.label} f={f} />)}</dl>}
          </section>
        )}
        {item.impacts.length > 0 && (
          <section className="stack" style={{ gap: 8 }} aria-labelledby={`${hid}-impacts`}>
            <h3 id={`${hid}-impacts`} className="deck-h">Before you decide</h3>
            <Table label="What changes either way" tableClassName="deck-impacts">
              <caption className="sr-only">What changes if you approve or reject</caption>
              <thead><tr><th scope="col"><span className="sr-only">What</span></th><th scope="col">Now</th><th scope="col" className="yes">If you approve</th><th scope="col">If you reject</th></tr></thead>
              <tbody>{item.impacts.map((r, i) => <tr key={i}><th scope="row">{r.label}</th><td>{r.now}</td><td className="yes">{r.approve}</td><td>{r.reject}</td></tr>)}</tbody>
            </Table>
          </section>
        )}
        {item.unlocks.length > 0 && (
          <section className="stack" style={{ gap: 8 }} aria-labelledby={`${hid}-unlocks`}>
            <h3 id={`${hid}-unlocks`} className="deck-h">What this unlocks</h3>
            <UnlockMap lanes={item.unlocks} gate={{ word: 'Needs you', tone: 'pending', detail: 'Nothing here starts until you decide.' }} />
          </section>
        )}
        {(item.asks.length > 0 || p?.rationale) && (
          <section aria-labelledby={`${hid}-why`}>
            <h3 id={`${hid}-why`} className="deck-h">Why it needs you</h3>
            <Expandable label={['Show the rules and the reason', 'Hide the rules and the reason']}>
              <div className="stack" style={{ gap: 8, marginTop: 8 }}>
                {item.asks.length > 0 && <ul className="deck-asks">{item.asks.map((a) => <li key={a.id}>{a.text}</li>)}</ul>}
                {p?.rationale && <div className="stack" style={{ gap: 4 }}><div className="faint">Why {item.from.kind === 'agent' ? `${item.from.name} suggests` : 'they suggest'} it</div><div className="note">{p.rationale}</div></div>}
              </div>
            </Expandable>
          </section>
        )}
        {(technical.length > 0 || p) && (
          <div>
            <Expandable label={['Show the technical details', 'Hide the technical details']}>
              <div className="stack" style={{ gap: 10, marginTop: 8 }}>
                {technical.length > 0 && <dl className="facts">{technical.map((f) => <FactRow key={f.label} f={f} />)}</dl>}
                {p && <Technical p={p} />}
              </div>
            </Expandable>
          </div>
        )}
      </div>
      <section className="deck-dock" aria-labelledby={`${hid}-do`}>
        <h3 id={`${hid}-do`} className="sr-only">What you can do</h3>
        <ErrorBanner error={error} />
        <div className="deck-actions">
          {item.actions.map((a) => (
            <ActionRow key={a.id} a={a} busy={busy} inline={!inDialog(a)} values={inputs[a.id] ?? {}} onInput={setInput(a)} onStart={() => start(a)} />
          ))}
        </div>
      </section>
      <ConfirmDialog open={!!confirming} title={confirming ? (confirming.id === 'stand' ? 'Stop asking for sign-offs like this one?' : confirming.id === 'changes' ? `Ask ${item.from.name} for changes?` : `${confirming.label}: ${item.title}?`) : ''}
        confirmLabel={confirming?.id === 'reject' ? 'Reject proposal' : confirming?.id === 'stand' ? 'Decide them for me' : confirming?.id === 'changes' ? 'Send it back' : confirming?.label ?? ''}
        tone={confirming?.tone === 'danger' ? 'danger' : 'primary'} busy={busy} onConfirm={() => confirming && act(confirming)} onCancel={() => setConfirming(null)}>
        <p style={{ margin: 0 }}>{confirming?.effect}</p>
        {confirming?.id === 'approve' && item.unlocks.length > 1 && (
          <><p style={{ margin: '8px 0 0' }}>Approving starts:</p><ul style={{ margin: '4px 0 0' }}>{item.unlocks.map((l, i) => <li key={i}>{l.unlock}</li>)}</ul></>
        )}
        {confirming?.id === 'stand' && (
          <>
            {item.asks.length > 0 && <><p style={{ margin: '8px 0 0' }}>Alike means the same kind of change, from {item.from.name}, for these reasons:</p><ul style={{ margin: '4px 0 0' }}>{item.asks.map((a) => <li key={a.id}>{a.text}</li>)}</ul></>}
            {confirming.inputs.map((i) => <InputField key={i.name} i={i} value={inputs.stand?.[i.name] ?? ''} onInput={setInput(confirming)} />)}
          </>
        )}
        {confirming?.id === 'changes' && confirming.inputs.map((i) => <InputField key={i.name} i={i} value={inputs.changes?.[i.name] ?? ''} onInput={setInput(confirming)} />)}
        {confirming?.id === 'reject' && (
          <Textarea label="Why are you rejecting it?" hint="Optional, but it helps the next proposal." value={note} onChange={(e) => setNote(e.target.value)} />
        )}
      </ConfirmDialog>
    </article>
  );
}

// Ask about this (docs/design/inbox.md): the Inbox agent by default, or another
// agent that answers questions, each its own conversation. An opinion only:
// the person still presses the button.
function AskAbout({ itemId, advisers }: { itemId: string; advisers: Inbox['advisers'] }) {
  const [who, setWho] = useState('');
  const name = advisers.find((a) => a.id === who)?.name;
  const picker = advisers.length > 0 && (
    <Select label="Ask" value={who} onValueChange={setWho} options={[{ value: '', label: 'The Inbox agent' }, ...advisers.map((a) => ({ value: a.id, label: a.name }))]} />
  );
  return (
    <AskAgent key={who} base={`/v1/inbox/items/${encodeURIComponent(itemId)}`} title="Ask about this" fallbackName={name ?? 'Inbox agent'} className="deck-ask"
      query={who ? `?agent=${encodeURIComponent(who)}` : ''} body={who ? { agent_id: who } : undefined} extra={picker}
      starters={['What would you do?']}
      hint={who ? '{name} gives an opinion from its own work. You decide.' : '{name} explains the item and says which button it would press. You decide.'} working="is reading the item" />
  );
}

// Your standing decisions (ADR 0038): what each covers, how often it has decided, and Revoke.
function Standing() {
  const s = useApi<{ items: Array<{ id: string; label: string; decision: 'approve' | 'reject'; created_at: string; decided: number }> }>('/v1/inbox/standing');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const revoke = async (id: string) => {
    setBusy(id); setError(null);
    try { await api(`/v1/inbox/standing/${id}/revoke`, { method: 'POST' }); toast('Revoked. Sign-offs like these come to you again.', 'ok'); await s.reload(); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  return (
    <section className="deck-card" aria-labelledby="standing-h">
      <div className="deck-body stack">
        <h2 id="standing-h" tabIndex={-1}>Decided for you</h2>
        <p className="deck-meaning">Sign-offs you told Mazu to stop asking about. Each one is decided the way you chose, as your own sign-off, while it matches the same kind of change, from the same agent or person, for the same reasons.</p>
        <ErrorBanner error={s.error ?? error} />
        {s.loading && !s.data ? <ListSkeleton rows={3} label="Loading your standing decisions" /> : !s.data?.items.length ? (
          <Empty title="Nothing is decided for you">On a sign-off, Stop asking for these sets one.</Empty>
        ) : (
          <ul className="deck-rows">
            {s.data.items.map((d) => (
              <li key={d.id} className="deck-standing">
                <div className="grow">
                  <strong>{d.decision === 'approve' ? 'Approve' : 'Reject'}: {d.label}</strong>
                  <div className="faint small">Set <Time iso={d.created_at} /> · decided {d.decided} {d.decided === 1 ? 'time' : 'times'} since</div>
                </div>
                <Button type="button" variant="secondary" size="sm" disabled={busy === d.id} onClick={() => void revoke(d.id)} aria-label={`Revoke: ${d.label}`}>Revoke</Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function FactRow({ f }: { f: Fact }) {
  return <><dt>{f.label}</dt><dd>{f.href ? <Link to={f.href}>{f.value}</Link> : f.value}</dd></>;
}

function InputField({ i, value, onInput }: { i: ActionInput; value: string; onInput: (name: string, v: string) => void }) {
  const label = i.required ? `${i.label} (required)` : i.label;
  return (
    i.type === 'select'
      ? <Select label={label} hint={i.hint ?? undefined} value={value} onValueChange={(v) => onInput(i.name, v)} options={i.options ?? []} />
      : i.type === 'textarea'
      ? <Textarea label={label} hint={i.hint ?? undefined} required={i.required} rows={i.name === 'body_text' ? 10 : 3} maxRows={i.name === 'body_text' ? 20 : 8} value={value} onChange={(e) => onInput(i.name, e.target.value)} />
      : <Input label={label} hint={i.hint ?? undefined} required={i.required} type={i.type === 'number' ? 'number' : 'text'} inputMode={i.type === 'number' ? 'decimal' : undefined} min={i.type === 'number' ? 0 : undefined} step={i.type === 'number' ? 'any' : undefined} value={value} onChange={(e) => onInput(i.name, e.target.value)} />
  );
}

function ActionRow({ a, busy, inline, values, onInput, onStart }: { a: InboxAction; busy: boolean; inline: boolean; values: Record<string, string>; onInput: (name: string, v: string) => void; onStart: () => void }) {
  const effectId = useId();
  const missing = inline && a.inputs.some((i) => i.required && !values[i.name]);
  return (
    <div className={`deck-action${inline && a.inputs.length ? ' has-inputs' : ''}`}>
      {inline && a.inputs.map((i) => <InputField key={i.name} i={i} value={values[i.name] ?? ''} onInput={onInput} />)}
      <Button variant={a.tone === 'primary' ? 'primary' : a.tone === 'danger' ? 'danger' : 'secondary'} disabled={busy || !!a.disabled_reason || missing} aria-describedby={effectId} onClick={onStart}>
        {a.id === 'approve' && <Icon name="check" />}{a.id === 'reject' && <Icon name="x" />}{a.label}{a.confirm ? '…' : ''}
      </Button>
      <p id={effectId} className="small muted deck-effect">{a.disabled_reason ? <><strong>Not now:</strong> {a.disabled_reason} </> : null}{a.effect}</p>
    </div>
  );
}

// What the toast says after an action, from what actually happened.
function message(item: InboxItem, a: InboxAction, out: { outcome: Outcome | null; routed: unknown }, values: Record<string, string> | undefined): string {
  if (a.id === 'reject') return 'Rejected. Nothing was saved.';
  if (a.id === 'changes') return `Sent back to ${item.from.name}. Its new draft comes here with what changed.`;
  if (a.id === 'stand') return values?.decision === 'reject' ? 'Rejected. Like sign-offs are rejected for you from now on.' : 'Approved. Like sign-offs are approved for you from now on.';
  if (a.id === 'edit' && out.outcome) return out.outcome.state === 'pending_approval' ? 'Your version is proposed. It waits for a sign-off.' : outcomeMessage(out.outcome, 'Your version was sent');
  if (a.id === 'done') return 'Closed as done';
  if (a.id === 'not_needed') return 'Closed as not needed';
  if (out.routed) return 'Sent to the agent';
  if (!out.outcome) return 'Done';
  if (item.source === 'approval') return out.outcome.state === 'pending_approval' ? 'Signed off. Waiting for a second person.' : outcomeMessage(out.outcome, 'Approved and saved');
  return out.outcome.state === 'pending_approval' ? `${a.label}: proposed. It waits for a sign-off.` : outcomeMessage(out.outcome, `${a.label}: done`);
}

function Body({ p: proposal }: { p: Proposal }) {
  const a = { ...proposal };
  const p = a.payload;
  switch (a.schema_id) {
    case 'import.batch': return <ImportSummary dry={a.computed?.import_dry_run} source={p.source} policy={p.duplicate_policy} changed={p.amendments?.length ?? 0} />;
    case 'party.merge': return <MergeCompare survivorId={p.survivor_id} mergedId={p.merged_id} match={a.computed?.merge_plan?.match} explanation={p.explanation} />;
    case 'party.mark_distinct': return <div className="stack" style={{ gap: 6 }}><div><PartyName id={p.party_ids[0]} /> and <PartyName id={p.party_ids[1]} /> are different.</div><div className="muted small">Reason: {p.reason}</div></div>;
    case 'party.create': {
      const dedup = a.computed?.dedup;
      return (
        <div className="stack" style={{ gap: 8 }}>
          <div><strong>{p.kind === 'person' ? [p.given_name, p.family_name].filter(Boolean).join(' ') : p.org_name}</strong> <span className="muted">· {[p.emails?.[0]?.address, p.phones?.[0]?.number].filter(Boolean).join(' · ')}</span></div>
          {dedup?.candidates?.length > 0 && (
            <div className="small">
              <div className="muted">Looks like:</div>
              {dedup.candidates.map((c: any) => <div key={c.party_id} className="row"><PartyName id={c.party_id} /><Score value={c.score} /><span className="faint">{c.reasons.join(', ')}</span></div>)}
            </div>
          )}
        </div>
      );
    }
    case 'party.bulk_edit': return <div className="small">{p.party_ids.length} contacts: {[p.archive && 'archive', p.tags_add && `add tags ${p.tags_add.join(', ')}`, p.tags_remove && `remove tags ${p.tags_remove.join(', ')}`, p.set && Object.entries(p.set).map(([k, v]) => `${k} → ${v}`).join(', ')].filter(Boolean).join(' · ')}{p.reason ? <div className="muted">Reason: {p.reason}</div> : null}</div>;
    case 'config.agent_status': return <div className="small">Change the agent to <Badge tone="accent">{STATUS_WORD[p.status] ?? p.status}</Badge>{p.status === 'active' && <div className="muted">Active: what it proposes inside its limits is saved or sent. An agent that works for a person still sends each draft to them first.</div>}{p.reason ? <div className="muted">Reason: {p.reason}</div> : null}</div>;
    case 'config.agent_envelope': return (
      <div className="stack" style={{ gap: 6 }}>
        {p.promote_to && <div className="small">Move the agent to <Badge tone="accent">{STATUS_WORD[p.promote_to] ?? p.promote_to}</Badge></div>}
        <div className="small">What it may do on its own: {Object.entries(p.envelope).map(([k, v]) => `${words(k)} ${String(v)}`).join(' · ')}</div>
      </div>
    );
    case 'config.agent_onboard': return <div className="small">Changes to the agent: {changed(p, ['agent_id', 'reason'])}</div>;
    case 'config.tenant': {
      const d = a.computed?.bench_disclosure;
      return (
        <div className="stack" style={{ gap: 6 }}>
          <div className="small">Changes: {changed(p, ['reason'])}</div>
          {p.signoff_policy && <Alert role="note" tone="warning">{p.signoff_policy.self_sign.length ? `An admin will change these alone, with no second signature: ${p.signoff_policy.self_sign.map((s: string) => schemaLabel(s)).join(', ')}.` : 'Another admin will sign every settings change.'}</Alert>}
          {d?.changes_opt_in && <Alert role="note" tone={d.opt_in ? 'warning' : 'info'}>{d.opt_in ? 'Opting in shares' : 'Opting out stops sharing'}: {d.shared.join(', ')}. {d.note}</Alert>}
        </div>
      );
    }
    case 'bench.advisory': {
      const e = a.computed?.bench_evidence;
      return <div className="stack" style={{ gap: 6 }}><div className="small"><strong>{e?.title ?? p.metric_id}</strong> · {p.period}: {p.finding}</div>{e?.distribution && <div className="small muted">This workspace: {e.value ?? '–'}. Cohort of {e.distribution.n_tenants}: median {e.distribution.p50 ?? '?'}, 10th to 90th percentile {e.distribution.p10 ?? '?'} to {e.distribution.p90 ?? '?'}. Engines {e.engines?.aggregate}, {e.engines?.anomaly}.</div>}{p.recommendation && <div className="note">{p.recommendation}</div>}</div>;
    }
    case 'config.webhook_events': {
      const c = a.computed?.webhook_events_check;
      if (p.action === 'end') return <div className="small">Stop sending events to <strong>{p.endpoint}</strong>.</div>;
      return (
        <div className="stack" style={{ gap: 6 }}>
          <div className="small">From now on, send each of these to <strong>{p.host}</strong> as it happens, with the record it is about. Nobody looks at each send.</div>
          <ul className="small" style={{ margin: 0 }}>{(c?.events ?? p.topics.map((name: string) => ({ name, description: name }))).map((e: { name: string; description: string }) => <li key={e.name}>{e.description}</li>)}</ul>
        </div>
      );
    }
    case 'config.agent_hire': return <div className="small">Hire <strong>{words(String(p.template_id).replace(/^(agents|local)\//, ''))}</strong> to work for {PRINCIPAL_WORD[p.principal_type] ?? 'the company'} (named above). It starts on trial, so a person checks its work first.</div>;
    case 'config.playbook_author': return (
      <div className="stack" style={{ gap: 8 }}>
        <div><strong>{p.title}</strong> <Badge tone="accent" style={{ textTransform: 'capitalize' }}>{p.class}</Badge><div className="muted small">{p.description}</div></div>
        <div className="small"><span className="faint">May propose:</span> {(p.allowed_schemas ?? []).map((s: string) => schemaLabel(s)).join(', ') || 'Nothing (read-only)'} · <span className="faint">reads:</span> {(p.scopes ?? []).join(', ') || 'nothing'} · <span className="faint">works for:</span> {(p.principal_types ?? []).map((t: string) => PRINCIPAL_WORD[t] ?? t).join(', ')}</div>
        <div className="small"><span className="faint">Eval cases:</span> {(p.evals ?? []).length}. Signing this signs the playbook; nothing hires it until the check passes.</div>
        <Expandable label={['Show the procedure', 'Hide the procedure']}><pre className="json note">{p.skill}</pre></Expandable>
      </div>
    );
    case 'config.dashboard': return <div className="small">{p.action === 'publish' ? 'Share the dashboard as a signed version' : p.action === 'draft' ? `A draft dashboard: ${p.name}` : 'Archive the dashboard'}{p.definition && <> with {p.definition.widgets.length} widgets: {p.definition.widgets.map((w: any) => w.title ?? w.report).join(', ')}</>}{p.shared_with && <div className="muted">Shared with {p.shared_with.workspace ? 'the whole workspace' : [...(p.shared_with.roles ?? []), ...(p.shared_with.user_ids ?? []).map(() => 'a person')].join(', ') || 'no one yet'}</div>}</div>;
    case 'quote.create': case 'quote.submit': {
      const price = a.computed?.quote_price ?? a.computed?.quote_check?.pricing;
      return price ? <div className="small">Total {money(price.total_minor, price.currency)} · discount {money(price.discount_minor, price.currency)} · tax {money(price.tax_minor, price.currency)}{price.envelope_breaches?.length ? <Alert role="note" tone="warning">Outside the agent's limits: {price.envelope_breaches.map((b: any) => b.message ?? b.key).join('; ')}</Alert> : null}</div> : null;
    }
    case 'comms.email_send': case 'comms.email_broadcast': {
      const prev = a.computed?.preview;
      return prev ? <div className="stack" style={{ gap: 6 }}><div className="small"><span className="faint">To:</span> {prev.to ?? `${a.computed?.audience?.count ?? ''} people`} · <span className="faint">Subject:</span> {prev.subject}</div><div className="note">{prev.text ?? prev.body_text ?? ''}</div></div> : null;
    }
    case 'asset.create': case 'asset.version': {
      // The template as a sample recipient would read it, and what kind of change it is (docs/plans/asset-library-plan.md).
      const prev = a.computed?.asset_render?.preview;
      const change = a.computed?.asset_diff?.change_class;
      return (
        <div className="stack" style={{ gap: 6 }}>
          <div className="small">{p.name ?? 'A new version'}{change ? <> · <Badge>{change === 'content' ? 'Words changed' : change}</Badge></> : null}{p.asset_id ? <> · <Link to={`/library/${p.asset_id}?tab=versions`}>Open in the Library</Link></> : null}</div>
          {prev && <><div className="small"><span className="faint">Subject:</span> {prev.subject}</div><div className="note">{prev.text}</div></>}
        </div>
      );
    }
    case 'asset.status': return <div className="small">Mark the template <strong>{p.status}</strong>{p.reason ? `: ${p.reason}` : ''} · <Link to={`/library/${p.asset_id}`}>Open in the Library</Link></div>;
    case 'deal.create': case 'deal.close': case 'deal.move_stage': return <div className="small">{p.title ?? 'This deal'}{p.value ? ` · ${money(p.value.minor, p.value.currency)}` : ''}{p.outcome ? ` · ${p.outcome}` : ''}</div>;
    case 'party.update': return <div className="small"><PartyName id={p.party_id} />: changes {changed(p, ['party_id', 'base_version'])}</div>;
    case 'site.change': return <SiteChangeBody proposalId={a.id} payload={p} computed={a.computed} />;
    default: return null;
  }
}

export function ImportSummary({ dry, source, policy, changed = 0 }: { dry: any; source?: any; policy?: string; changed?: number }) {
  if (!dry) return null;
  const stat = (n: number, label: string, tone = '') => <div className={`${cardClass} stat`} style={{ boxShadow: 'none' }}><div className={`num ${tone}`} style={{ fontSize: 22 }}>{n.toLocaleString()}</div><div className="label">{label}</div></div>;
  return (
    <div className="stack" style={{ gap: 10 }}>
      {source && <div className="faint">From {systemName(source.system)} · {source.file?.split('/').pop()}</div>}
      <div className="stats">
        {stat(dry.rows, 'New rows')}
        {changed > 0 && stat(changed, 'Changed in the other system')}
        {stat(dry.merges_in_batch.length, 'Duplicates inside the file')}
        {stat(dry.merges_existing.length, 'Match existing contacts')}
        {stat(dry.possible.length, 'Possible duplicates for review')}
      </div>
      {dry.errors.length > 0 && <Alert role="note" tone="danger">{dry.errors.slice(0, 5).join(' · ')}</Alert>}
      {dry.unknown_owners.length > 0 && <Alert role="note" tone="danger">Unknown owners: {dry.unknown_owners.join(', ')}</Alert>}
      {dry.missing_fields.no_email_no_phone.length > 0 && <Alert role="note" tone="warning">{dry.missing_fields.no_email_no_phone.length} people have neither an email nor a phone. They will still be imported.</Alert>}
      <div className="muted small">
        {policy === 'review'
          ? 'Duplicates will be imported as separate records and queued for the Merge reviewer agent.'
          : 'Duplicates at 0.9 or above will be merged into one record each; nothing is deleted. Possible duplicates go to the Merge reviewer agent.'}
      </div>
      {dry.merges_in_batch.length > 0 && (
        <details>
          <summary className="small" style={{ cursor: 'pointer' }}>Show the {dry.merges_in_batch.length} duplicate pairs inside the file</summary>
          <Table label="Duplicate pairs inside the file" style={{ marginTop: 8 }}>
            <caption className="sr-only">Duplicate pairs inside the file</caption>
            <thead><tr><th scope="col">Keeps</th><th scope="col">Folds in</th><th scope="col">Score</th><th scope="col">Why</th></tr></thead>
            <tbody>{dry.merges_in_batch.slice(0, 100).map((m: any) => <tr key={m.merged}><td className="mono">{m.survivor}</td><td className="mono">{m.merged}</td><td>{m.score}</td><td className="small">{m.reasons.join(', ')}</td></tr>)}</tbody>
          </Table>
        </details>
      )}
    </div>
  );
}

function MergeCompare({ survivorId, mergedId, match, explanation }: { survivorId: string; mergedId: string; match?: any; explanation?: string }) {
  const [pair, setPair] = useState<[Party | null, Party | null] | null>(null);
  useEffect(() => { Promise.all([fetchParty(survivorId), fetchParty(mergedId)]).then((x) => setPair(x as [Party | null, Party | null])); }, [survivorId, mergedId]);
  const [keep, fold] = pair ?? [null, null];
  const points = (p: Party | null, kind: string) => p?.contact_points.filter((c) => c.kind === kind).map((c) => (kind === 'address' ? [c.value.street, c.value.city].filter(Boolean).join(', ') : c.value.raw)).join('; ') ?? '';
  const rows: Array<[string, (p: Party | null) => ReactNode]> = [
    ['Name', (p) => p && <Link to={`/contacts/${p.id}`}>{p.display_name}</Link>],
    ['Email', (p) => points(p, 'email')],
    ['Phone', (p) => points(p, 'phone')],
    ['Address', (p) => points(p, 'address')],
    ['Dates', (p) => p?.occasions.map((o) => `${o.kind.replace('_', ' ')} ${o.on_date}`).join('; ')],
    ['Created', (p) => p && new Date(p.created_at).toLocaleDateString()],
  ];
  const text = (n: ReactNode) => (typeof n === 'string' ? n : '');
  return (
    <div className="stack" style={{ gap: 8 }}>
      {match && <div className="row small"><Badge tone={match.level === 'duplicate' ? 'danger' : 'warning'}>{match.level === 'duplicate' ? 'Likely duplicate' : 'Possible duplicate'}</Badge><Score value={match.score} /><span className="muted">{match.reasons?.join(', ')}</span></div>}
      {explanation && <div className="muted small">{explanation}</div>}
      {!pair ? <Loading label="Loading both records" /> : (
        <Table label="The two records side by side" tableClassName="compare-table">
          <caption className="sr-only">The two records side by side. Rows that differ are marked.</caption>
          <thead><tr><th scope="col"><span className="sr-only">Field</span></th><th scope="col">Keeps this record</th><th scope="col">Folds this one in</th></tr></thead>
          <tbody>{rows.map(([label, get]) => {
            const a = get(keep), b = get(fold);
            const differs = label !== 'Name' && label !== 'Created' && text(a) !== text(b);
            return (
              <tr key={label}>
                <th scope="row">{label}{differs && <span className="sr-only"> (differs)</span>}</th>
                <td className={differs ? 'differs' : ''}>{a || <span className="faint">None</span>}</td>
                <td className={differs ? 'differs' : ''}>{b || <span className="faint">None</span>}</td>
              </tr>
            );
          })}</tbody>
        </Table>
      )}
    </div>
  );
}

// What an admin may want before signing: the agent's evidence, the replay of a
// playbook change, and the exact proposal.
function Technical({ p }: { p: Proposal }) {
  const e = p.schema_id === 'config.agent_envelope' ? p.payload.evidence : null;
  return (
    <>
      {e && <div className="small muted">Evidence: {e.decided} decided, {e.edited} edited, {e.rejected} rejected, {e.stop_fires} stop fires over {e.periods} weeks · engine {e.engine?.id} {e.engine?.version}</div>}
      <ReplayDiff computed={p.computed} />
      <div className="faint small">The exact proposal</div>
      <pre className="json" tabIndex={0} role="region" aria-label="Payload">{JSON.stringify(p.payload, null, 2)}</pre>
    </>
  );
}

// Field names as words: budget, daily cap, signoff policy.
const words = (k: string) => k.replace(/[_-]/g, ' ');
const changed = (p: Record<string, unknown>, skip: string[]) => Object.keys(p).filter((k) => !skip.includes(k)).map(words).join(', ');
// Active is not "on its own": an agent that works for a person still sends each draft to them.
const STATUS_WORD: Record<string, string> = { shadow: 'on trial', active: 'active', paused: 'paused', suspended: 'suspended', retired: 'retired' };
const PRINCIPAL_WORD: Record<string, string> = { tenant: 'the company', team: 'a team', user: 'one person' };

// The replay a person sees before signing a change to a running agent.
function ReplayDiff({ computed }: { computed: any }) {
  const r = computed?.replay_diff;
  if (!r?.required) return null;
  if (!r.replay) return <Alert role="note" tone="warning">No replay ran for this change. {r.note}</Alert>;
  const rep = r.replay;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <Alert role="note" tone={rep.changed ? 'warning' : 'success'}>Replay of {rep.items} past items at v{rep.candidate?.playbook_version}: {rep.changed} would propose something different, {rep.decision_changes} would be decided differently.</Alert>
      {rep.diff?.length > 0 && (
        <Table label="Replay differences">
          <caption className="sr-only">What the candidate proposed differently</caption>
          <thead><tr><th scope="col">Work</th><th scope="col">Change</th><th scope="col">Before</th><th scope="col">Candidate</th></tr></thead>
          <tbody>{rep.diff.map((d: any, i: number) => <tr key={i}><td className="small">{d.task_kind}</td><td><Badge tone="warning">{d.change}</Badge></td><td className="small">{d.baseline.map((x: any) => `${x.schema_id} (${x.decision})`).join(', ') || 'nothing'}</td><td className="small">{d.candidate.map((x: any) => `${x.schema_id} (${x.decision})`).join(', ') || 'nothing'}</td></tr>)}</tbody>
        </Table>
      )}
    </div>
  );
}
